import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import SoundToggle from './SoundToggle';
import * as FiIcons from 'react-icons/fi';
import { playSoundIfEnabled } from '../utils/soundEffects';
import { getCurrentPasscode, getPasscodesForRange } from '../utils/passcodeGenerator';

const { FiHeart, FiShuffle, FiCoffee, FiInfo, FiLock, FiUnlock, FiArrowLeft, FiClock, FiTarget, FiAward, FiRotateCw, FiDelete, FiX, FiDollarSign, FiBox } = FiIcons;

const MainMenu = ({ onGameSelect }) => {
  const navigate = useNavigate();
  const [showPasscodeModal, setShowPasscodeModal] = useState(true);
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [validPasscode, setValidPasscode] = useState('');
  const [futurePasscodes, setFuturePasscodes] = useState([]);

  useEffect(() => {
    // Check if passcode was previously entered in this session
    const passcodeSaved = sessionStorage.getItem('passcodeVerified');
    if (passcodeSaved === 'true') {
      setShowPasscodeModal(false);
    }

    // Get the current valid passcode
    setValidPasscode(getCurrentPasscode());

    // Get a list of future passcodes for the next 30 days
    const today = new Date();
    const passcodes = getPasscodesForRange(today, 30);
    setFuturePasscodes(passcodes);
  }, []);

  const verifyPasscode = () => {
    // Check for admin access code (a special passcode to access admin panel)
    if (passcode.toUpperCase() === 'ADMIN') {
      navigate('/admin');
      return;
    }

    // Check if this is today's passcode
    if (passcode === validPasscode) {
      setShowPasscodeModal(false);
      sessionStorage.setItem('passcodeVerified', 'true');
      setError('');
      playSoundIfEnabled('success');
      return;
    }

    // Check if this is a future passcode
    const futurePasscode = futurePasscodes.find(p => p.passcode === passcode);
    if (futurePasscode) {
      setError(`Cảm ơn Ní đã đặt phòng tại Lux Homestay. Mã truy cập chỉ có hiệu lực trong ngày bạn đã đặt phòng!`);
      setPasscode('');
      playSoundIfEnabled('error');
      return;
    }

    // If not a valid passcode
    setError('Mã không chính xác. Vui lòng thử lại.');
    setPasscode('');
    playSoundIfEnabled('error');
  };

  const handleNavigate = (path) => {
    playSoundIfEnabled('navigationClick');
    navigate(path);
  };

  const handleNumpadClick = (num) => {
    if (passcode.length < 5) {
      setPasscode(prev => prev + num);
    }
  };

  const handleDelete = () => {
    setPasscode(prev => prev.slice(0, -1));
  };

  const handleClear = () => {
    setPasscode('');
  };

  // Handle keyboard input for passcode
  const handleKeyDown = (e) => {
    // Allow alphanumeric characters (numbers and letters)
    if (/^[a-zA-Z0-9]$/.test(e.key) && passcode.length < 5) {
      setPasscode(prev => prev + e.key.toUpperCase());
    }

    // Handle backspace/delete
    if (e.key === 'Backspace' || e.key === 'Delete') {
      handleDelete();
    }
    // Handle Enter key to submit
    else if (e.key === 'Enter') {
      verifyPasscode();
    }
    // Handle Escape key to clear
    else if (e.key === 'Escape') {
      handleClear();
    }
  };

  const games = [
    {
      id: 'cards',
      title: 'Thẻ Bài Tình Yêu',
      description: 'Trò chơi thẻ bài thú vị dành cho các cặp đôi',
      icon: FiHeart,
      color: 'from-pink-500 to-rose-500',
      path: '/cards'
    },
    {
      id: 'food-fun',
      title: 'Food & Fun Roulette',
      description: 'Khám phá ẩm thực và hoạt động thú vị tại TP.HCM',
      icon: FiCoffee,
      color: 'from-orange-400 to-red-500',
      path: '/food-fun'
    },
    {
      id: 'dice',
      title: 'Xúc Xắc Tình Yêu',
      description: 'Tung xúc xắc để nhận thử thách nóng bỏng',
      icon: FiShuffle,
      color: 'from-purple-500 to-blue-500',
      path: '/dice-game'
    },
    {
      id: 'strip',
      title: 'Quây Tù Tì Nóng Bỏng',
      description: 'Trò chơi quây tù tì với thử thách táo bạo',
      icon: FiTarget,
      color: 'from-red-500 to-pink-600',
      path: '/strip-game'
    },
    {
      id: 'timer',
      title: 'Timer Challenge',
      description: 'Hoàn thành thử thách trong thời gian giới hạn',
      icon: FiClock,
      color: 'from-yellow-400 to-orange-500',
      path: '/timer-challenge'
    },
    {
      id: 'truth-dare',
      title: 'Truth or Dare',
      description: 'Trò chơi truth or dare kinh điển với twist mới',
      icon: FiHeart,
      color: 'from-indigo-500 to-purple-600',
      path: '/truth-or-dare'
    },
    {
      id: 'couple-quiz',
      title: 'Couple Quiz',
      description: 'Kiểm tra mức độ hiểu biết về đối phương',
      icon: FiAward,
      color: 'from-teal-400 to-blue-500',
      path: '/couple-quiz'
    },
    {
      id: 'bau-cua',
      title: 'Lắc Bầu Cua',
      description: 'Trò chơi bầu cua truyền thống với thử thách',
      icon: FiDollarSign,
      color: 'from-green-400 to-teal-500',
      path: '/bau-cua'
    },
    {
      id: 'mystery-box',
      title: 'Hộp Bí Ẩn',
      description: 'Chọn ngẫu nhiên người chơi và thử thách từ hộp bí ẩn',
      icon: FiBox,
      color: 'from-purple-400 to-indigo-500',
      path: '/mystery-box'
    }
  ];

  if (showPrivacy) {
    return (
      <div className="min-h-screen p-4 flex flex-col">
        <motion.button
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => {
            playSoundIfEnabled('navigationClick');
            setShowPrivacy(false);
          }}
          className="self-start mb-4 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl" />
        </motion.button>
        <div className="flex-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl w-full bg-white/10 backdrop-blur-lg p-8 rounded-3xl"
          >
            <h1 className="text-3xl font-bold text-white text-center mb-6">
              Điều Khoản & Quyền Riêng Tư
            </h1>
            <div className="space-y-6 text-white">
              <div>
                <h2 className="text-xl font-bold mb-2">1. Giới Hạn Độ Tuổi:</h2>
                <p className="text-pink-200">
                  Ứng dụng này chỉ dành cho người từ 18 tuổi trở lên. Bằng việc sử dụng ứng dụng này, bạn xác nhận mình đã đủ tuổi theo quy định.
                </p>
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2">2. Quyền Sở Hữu:</h2>
                <p className="text-pink-200">
                  Game này được tạo và sở hữu độc quyền bởi Lux Homestay. Mọi hình thức sao chép, phân phối lại mà không có sự cho phép đều bị nghiêm cấm.
                </p>
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2">3. Nội Dung:</h2>
                <p className="text-pink-200">
                  Trò chơi có chứa nội dung người lớn và chỉ nên chơi trong không gian riêng tư. Người dùng tự chịu trách nhiệm về việc sử dụng trò chơi này.
                </p>
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2">4. Quyền Riêng Tư:</h2>
                <p className="text-pink-200">
                  Chúng tôi không thu thập, lưu trữ hay chia sẻ bất kỳ thông tin cá nhân nào của người dùng. Trò chơi được chơi hoàn toàn ngoại tuyến.
                </p>
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2">5. Miễn Trừ Trách Nhiệm:</h2>
                <p className="text-pink-200">
                  Lux Homestay không chịu trách nhiệm về bất kỳ hậu quả nào phát sinh từ việc sử dụng trò chơi này. Người dùng tự chịu trách nhiệm về quyết định và hành động của mình.
                </p>
              </div>
              <div className="pt-4 border-t border-white/20">
                <p className="text-center text-pink-200">
                  © 2023 Lux Homestay. Bảo lưu mọi quyền.
                </p>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  playSoundIfEnabled('navigationClick');
                  setShowPrivacy(false);
                }}
                className="w-full mt-4 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold py-4 px-6 rounded-2xl shadow-lg transition-all duration-300"
              >
                Tôi Đã Hiểu & Đồng Ý
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <AnimatePresence>
        {showPasscodeModal && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
          >
            <div
              className="bg-gradient-to-br from-purple-900 to-pink-900 p-8 rounded-3xl w-full max-w-md"
              onKeyDown={handleKeyDown}
              tabIndex="0"
            >
              <div className="text-center mb-6">
                <div className="flex justify-center mb-4">
                  <motion.img
                    src="https://app1.sharemyimage.com/2025/06/10/logo_1736360407_LUX-HOME-22-1024x368.png"
                    alt="Lux Homestay"
                    className="h-20 object-contain filter drop-shadow-lg brightness-150 contrast-125"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
                <SafeIcon icon={FiLock} className="text-5xl text-pink-400 mb-4" />
                <h2 className="text-2xl font-bold text-white">Nhập Mã Truy Cập</h2>
                <p className="text-pink-200 mt-2">
                  Vui lòng nhập mã truy cập để tiếp tục
                </p>
              </div>
              <div className="space-y-4">
                <div className="relative">
                  <input
                    type="text"
                    value={passcode}
                    onChange={(e) => {
                      const alphanumericValue = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                      if (alphanumericValue.length <= 5) {
                        setPasscode(alphanumericValue);
                      }
                    }}
                    className="w-full p-4 rounded-xl bg-white/10 text-white border border-pink-300/30 focus:border-pink-400 focus:outline-none text-center text-2xl tracking-widest"
                    maxLength={5}
                    autoFocus
                  />
                  {passcode && (
                    <button
                      onClick={handleClear}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 hover:text-white"
                    >
                      <SafeIcon icon={FiX} className="text-xl" />
                    </button>
                  )}
                </div>
                {error && (
                  <p className="text-red-400 text-sm mt-2">{error}</p>
                )}
                {/* Numpad */}
                <div className="grid grid-cols-3 gap-3 mt-4">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map((num) => (
                    <motion.button
                      key={num}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleNumpadClick(num)}
                      className="bg-white/10 hover:bg-white/20 text-white font-bold text-2xl py-3 rounded-xl transition-all"
                    >
                      {num}
                    </motion.button>
                  ))}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleDelete}
                    className="bg-white/10 hover:bg-white/20 text-white py-3 rounded-xl flex items-center justify-center"
                  >
                    <SafeIcon icon={FiDelete} className="text-2xl" />
                  </motion.button>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={verifyPasscode}
                  className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold py-4 px-6 rounded-xl shadow-lg transition-all duration-300 flex items-center justify-center gap-2 mt-4"
                >
                  <SafeIcon icon={FiUnlock} className="text-xl" />
                  Mở Khóa
                </motion.button>
                <p className="text-xs text-pink-200 text-center mt-4">
                  © 2023 Lux Homestay. Bảo lưu mọi quyền.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl w-full"
      >
        <div className="flex flex-col items-center mb-8">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="mb-6"
          >
            <img
              src="https://app1.sharemyimage.com/2025/06/10/logo_1736360407_LUX-HOME-22-1024x368.png"
              alt="Lux Homestay"
              className="h-24 md:h-32 object-contain filter drop-shadow-xl brightness-150 contrast-125 bg-white/10 backdrop-blur-lg rounded-2xl p-3"
            />
          </motion.div>
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            className="text-center"
          >
            <h1 className="text-6xl font-bold text-white mb-4">
              💕 Love & Fun 💕
            </h1>
            <p className="text-xl text-pink-200">
              Trò chơi dành cho các cặp đôi muốn khám phá nhau và thành phố
            </p>
          </motion.div>
          <div className="absolute top-4 right-4">
            <SoundToggle className="bg-white/10" />
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {games.map((game, index) => (
            <motion.button
              key={game.id}
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleNavigate(game.path)}
              className={`
                bg-gradient-to-r ${game.color} p-6 rounded-2xl shadow-2xl text-white text-left relative overflow-hidden transform transition-all duration-300 hover:shadow-3xl
              `}
            >
              <div className="relative z-10 flex items-center gap-4">
                <div className="bg-white/20 p-3 rounded-full">
                  <SafeIcon icon={game.icon} className="text-2xl" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-1">{game.title}</h3>
                  <p className="text-sm opacity-90">{game.description}</p>
                </div>
              </div>
              <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-12 translate-x-12" />
            </motion.button>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-center mt-8"
        >
          <p className="text-pink-200 text-sm mb-2">
            ⚠️ Chỉ dành cho người trên 18 tuổi - Chơi có trách nhiệm
          </p>
          <button
            onClick={() => {
              playSoundIfEnabled('navigationClick');
              setShowPrivacy(true);
            }}
            className="text-pink-300 text-xs underline flex items-center justify-center mx-auto"
          >
            <SafeIcon icon={FiInfo} className="mr-1 text-xs" />
            Điều khoản & Quyền riêng tư
          </button>
          <p className="text-pink-200 text-xs mt-4">
            © 2023 Lux Homestay. Bảo lưu mọi quyền.
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default MainMenu;